import { memo, useEffect, useRef, useState, forwardRef, useImperativeHandle, useMemo } from "react";
import { makeStyles } from "@mui/styles";
import { Grid2, Typography } from "@mui/material";
import { Formik } from "formik";
import { TimePicker } from '@mui/x-date-pickers';
import NumberedInput from "./NumberedInput.js";

import { validations } from "../utils/index.js";

// eslint-disable-next-line import/no-named-as-default
import Input from "./Input.js";
import DeletableInput from "./DeletableInput.js";
import { SecondaryBackgroundButton } from "./Buttons.js";
import Dropdown from "./Dropdown.js";
import Checkbox from "./Checkbox.js";
import RadioButtons from "./RadioButtons.js";
import Slider from "./Slider.js";
import Switch from "./Switch.js";
import DatePicker from "./DatePicker.js";
import AddButton from "./AddButton.js";
import MultiSelectDropdown from "./MultiSelectDropdown.js";

const useStyles = makeStyles((theme) => ({
	form: {
		width: "100%",
		display: "flex",
		justifyContent: "space-evenly",
		flexDirection: "column",
		alignItems: "center",
		textAlign: "center",
	},
	input: {
		color: "black",
		width: "100%",
		maxWidth: "600px",
		backgroundColor: theme.palette.third.main,
		opacity: 0.7,
		marginBottom: "10px",
		"&:hover": {
			opacity: 0.8,
			borderColor: theme.palette.secondary.main
		},
		"&:focus": {
			borderColor: theme.palette.secondary.main
		},
	},
	dropdown: {
		width: "100%",
		maxWidth: "600px",
		marginBottom: "10px",
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
	},
	checkboxBox: {
		width: "100%",
		maxWidth: "300px",
		marginBottom: "10px",
		display: "flex",
	},
	checkbox: {
		width: "100%",
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		color: "black",
	},
	radioBox: {
		width: "100%",
		maxWidth: "300px",
		marginBottom: "10px",
		display: "flex",
		flexDirection: "column",
		color: "white",
	},
	sliderBox: {
		width: "100%",
		maxWidth: "300px",
		marginBottom: "10px",
		display: "flex",
		flexDirection: "column",
		color: "white",
	},
	datepickerBox: {
		width: "100%",
		maxWidth: "600px",
		marginBottom: "10px",
		display: "flex",
		flexDirection: "column",
		color: "white",
		borderBottom: "1px solid",
		borderBottomColor: "black",
		"&:hover": {
			opacity: 0.8,
			borderColor: theme.palette.secondary.main
		},
	},
	switchBox: {
		width: "100%",
		maxWidth: "300px",
		marginTop: "10px",
		marginBottom: "10px",
		display: "flex",
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
		color: "white",
	},
	buttonTitle: {
		color: "white",
		letterSpacing: theme.spacing(0.1),
	},
	markLabel: {
		color: "white",
	},
	button: {
		width: "100%",
		maxWidth: "300px",
	},
}));

// Helper function to check if a field has a value
const fieldHasValue = (comp) => {
	switch (comp.customType) {
		case "input":
		case "deletable-input":
		case "numbered-input":
			return comp.value !== undefined && comp.value !== null && comp.value !== "";
		case "dropdown":
		case "checkbox":
		case "radio":
		case "slider":
		case "switch":
		case "multiselect-dropdown":
			return comp.defaultValue !== undefined && comp.defaultValue !== null && comp.defaultValue !== "";
		case "date-picker":
		case "hour-picker":
			return comp.value !== undefined && comp.value !== null;
		default:
			return true;
	}
};

// Helper function to check if a field is focusable
const isFocusableField = (comp) => {
	return ["input", "deletable-input", "numbered-input", "date-picker", "hour-picker"].includes(comp.customType)
		&& !comp.disabled
		&& !comp.isHidden;
};

const Form = forwardRef(({ disabled: dsb, content, validationSchema, onSubmit, onSubmitProps, toResetForm = true }, ref) => {
	const classes = useStyles();
	const [formContent, setFormContent] = useState(content);
	const [disabled, setDisabled] = useState(dsb);
	const formRef = useRef();

	useEffect(() => {
		setFormContent(content);
	}, [content]);

	useEffect(() => {
		setDisabled(dsb);
	}, [dsb]);

	// Find the first empty focusable field
	const firstEmptyFieldId = useMemo(() => {
		for (const comp of formContent) {
			if (isFocusableField(comp) && !fieldHasValue(comp)) {
				return comp.id;
			}
		}
		// If no empty field found, return the first focusable field
		for (const comp of formContent) {
			if (isFocusableField(comp)) {
				return comp.id;
			}
		}
		return null;
	}, [formContent]);

	useImperativeHandle(ref, () => ({
		getFormValues() {
			return formRef.current.values;
		},
		setFieldValue(field, value) {
			return formRef.current.setFieldValue(field, value, false);
		},
	}));

	// Helper to determine if a component should be auto-focused
	const shouldAutoFocus = (comp) => {
		return comp.id === firstEmptyFieldId;
	};

	return (
		<Formik
			enableReinitialize
			innerRef={formRef}
			initialValues={formContent.reduce((a, v) => (
				v.customType === "input"
					? { ...a, [v.id]: v.value || "" }
					: (v.customType === "dropdown"
						|| v.customType === "checkbox"
						|| v.customType === "radio"
						|| v.customType === "slider"
						|| v.customType === "switch"
						? { ...a, [v.id]: v.defaultValue }
						: (v.customType === "multiselect-dropdown"
							? { ...a, [v.id]: v.defaultValue || [] }
							: (v.customType === "date-picker"
								|| v.customType === "hour-picker"
								? { ...a, [v.id]: v.value || null }
								: a
							)
						)
					)
			), {})}
			validate={(values) => {
				const errors = {};
				formContent.forEach((comp) => {
					if (comp.required && (comp.customType === "date-picker" || comp.customType === "hour-picker")) {
						if (!values[comp.id] || values[comp.id] === null || values[comp.id] === undefined) {
							errors[comp.id] = "Υποχρεωτικό πεδίο";
						}
					}
				});
				return errors;
			}}
			validationSchema={validations?.[validationSchema] || null}
			validateOnChange={false}
			onSubmit={(...formikArgs) => {
				onSubmit(...formikArgs, onSubmitProps);
				const [, { resetForm, setSubmitting }] = formikArgs;
				if (toResetForm) resetForm();
				setSubmitting(false);
			}}
		>
			{(formikProps) => (
				<form className={classes.form} onSubmit={formikProps.handleSubmit}>
					{formContent.map((comp) => (
						<div
							key={comp.id}
							style={{ width: "100%", display: "flex", justifyContent: "center" }}
						>
							{comp.customType === "input" && !comp.isHidden
								&& (
									<Input
										autoFocus={shouldAutoFocus(comp)}
										required={comp.required}
										key={comp.id}
										id={comp.id}
										type={comp.type}
										multiline={comp.multiline}
										minRows={comp.minRows}
										maxRows={comp.maxRows}
										className={classes.input}
										variant={comp.variant || "standard"}
										color="secondary"
										label={comp.placeholder}
										InputProps={comp.inputProps}
										value={formikProps.values[comp.id]}
										error={Boolean(formikProps.errors[comp.id])}
										helperText={comp.helperText || formikProps.errors[comp.id]}
										disabled={disabled || comp.disabled}
										onChange={(event) => {
											formikProps.handleChange(event);
											if (comp.onChange) {
												comp.onChange(event);
											}
										}}
									/>
								)}
							{comp.customType === "deletable-input" && (
								<DeletableInput
									key={comp.id}
									id={comp.id}
									label={comp.placeholder}
									required={comp.required}
									variant={comp.variant || "standard"}
									className={classes.input}
									value={formikProps.values[comp.id]}
									autoFocus={shouldAutoFocus(comp)}
									onDelete={() => {
										const updatedFormContent = formContent.filter((item) => item.id !== comp.id);
										setFormContent(updatedFormContent);
									}}
									onChange={(event) => {
										formikProps.handleChange(event);
										if (comp.onChange) {
											comp.onChange(event);
										}
									}}
									error={Boolean(formikProps.errors[comp.id])}
									helperText={comp.helperText || formikProps.errors[comp.id]}
									disabled={disabled || comp.disabled}
								/>
							)}
							{comp.customType === "dropdown"
								&& (
									<Grid2 item className={classes.dropdown}>
										<Dropdown
											id={comp.id}
											items={comp.items}
											value={formikProps.values[comp.id] || ""}
											disabled={disabled || comp.disabled}
											size="medium"
											width="600px"
											filled={false}
											label={comp.label}
											background="secondary"
											placeholder={comp.placeholder}
											required={comp.required}
											onChange={(event) => {
												formikProps.handleChange({
													target: {
														name: comp.id,
														value: event.target.value,
													},
												});
												if (comp.onChange) {
													comp.onChange(event);
												}
											}}
										/>
									</Grid2>
								)}
							{comp.customType === "checkbox"
								&& (
									<Grid2 container item className={classes.checkboxBox}>
										<Grid2 item className={classes.checkbox}>
											<Typography>{comp.label}</Typography>
											<Checkbox
												key={comp.id}
												id={comp.id}
												checked={formikProps.values[comp.id]}
												size={comp.size}
												color={comp.color}
												sx={{
													color: `${comp.color}.main`,
													"&.Mui-checked": {
														color: `${comp.color}.main`,
													},
												}}
												icon={comp.icon}
												checkedIcon={comp.checkedIcon}
												disabled={disabled || comp.disabled}
												onChange={(event) => {
													formikProps.handleChange({
														target: {
															name: comp.id,
															value: !formikProps.values[comp.id],
														},
													});
													if (comp.onChange) {
														comp.onChange(event);
													}
												}}
											/>
										</Grid2>
										{Boolean(formikProps.errors[comp.id])
											&& (
												<Typography color="error" fontSize="small">{formikProps.errors[comp.id]}</Typography>
											)}
									</Grid2>
								)}
							{comp.customType === "radio"
								&& (
									<Grid2 key={comp.id} container item className={classes.radioBox}>
										<Typography textAlign="left">{comp.label}</Typography>
										<RadioButtons
											id={comp.label}
											value={formikProps.values[comp.id]}
											row={comp.row}
											color={comp.color}
											labelPlacement={comp.labelPlacement}
											disabled={disabled || comp.disabled}
											items={comp.items}
											onChange={(event) => {
												formikProps.handleChange({
													target: {
														name: comp.id,
														value: event.target.value,
													},
												});
												if (comp.onChange) {
													comp.onChange(event);
												}
											}}
										/>
										{Boolean(formikProps.errors[comp.id])
											&& (
												<Typography textAlign="left" color="error" fontSize="small">{formikProps.errors[comp.id]}</Typography>
											)}
									</Grid2>
								)}
							{comp.customType === "slider"
								&& (
									<Grid2 key={comp.id} container item className={classes.sliderBox}>
										<Typography textAlign="left">{comp.label}</Typography>
										<Slider
											iconBefore={comp.iconBefore}
											iconAfter={comp.iconAfter}
											color={comp.color || "secondary"}
											value={formikProps.values[comp.id]}
											min={comp.min}
											max={comp.max}
											marks={comp.marks}
											step={comp.step}
											size={comp.size}
											track={comp.track}
											valueLabelDisplay={comp.displayLabel}
											disabled={disabled || comp.disabled}
											onChange={(event) => {
												formikProps.handleChange({
													target: {
														name: comp.id,
														value: event.target.value,
													},
												});
												if (comp.onChange) {
													comp.onChange(event);
												}
											}}
										/>
										{Boolean(formikProps.errors[comp.id])
											&& (
												<Typography textAlign="left" color="error" fontSize="small">{formikProps.errors[comp.id]}</Typography>
											)}
									</Grid2>
								)}
							{comp.customType === "switch"
								&& (
									<Grid2 key={comp.id} container item className={classes.switchBox}>
										<Typography textAlign="left">{comp.label}</Typography>
										<Switch
											color={comp.color || "secondary"}
											checked={formikProps.values[comp.id]}
											size={comp.size}
											disabled={disabled || comp.disabled}
											onChange={(event) => {
												formikProps.handleChange({
													target: {
														name: comp.id,
														value: !formikProps.values[comp.id],
													},
												});
												if (comp.onChange) {
													comp.onChange(event);
												}
											}}
										/>
										{Boolean(formikProps.errors[comp.id])
											&& (
												<Typography textAlign="left" color="error" fontSize="small">{formikProps.errors[comp.id]}</Typography>
											)}
									</Grid2>
								)}
							{comp.customType === "date-picker"
								&& (
									<Grid2 key={comp.id} container item className={classes.datepickerBox}>
										<Typography textAlign="left" sx={{ color: "greyDark.main", fontSize: "13px" }}>
											{comp.label}{comp.required && " *"}
										</Typography>
										<DatePicker
											type={comp.type || "desktop"}
											value={formikProps.values[comp.id]}
											disabled={disabled || comp.disabled}
											required={comp.required}
											autoFocus={shouldAutoFocus(comp)}
											label={comp.sublabel || ""}
											views={comp.views || ["day"]}
											background={comp.background || "secondary"}
											color="greyDark"
											width={comp.width || "300px"}
											onAccept={comp.onAccept}
											onChange={(value) => {
												formikProps.handleChange({
													target: {
														name: comp.id,
														value,
													},
												});
												if (comp.onChange) {
													comp.onChange(value);
												}
											}}
										/>
										{Boolean(formikProps.errors[comp.id])
											&& (
												<Typography textAlign="left" color="error" fontSize="small">{formikProps.errors[comp.id]}</Typography>
											)}
									</Grid2>
								)}
							{comp.customType === "hour-picker" && (
								<Grid2 key={comp.id} container item className={classes.datepickerBox}>
									<Typography textAlign="left" sx={{ color: "greyDark.main", fontSize: "13px" }}>
										{comp.label}{comp.required && " *"}
									</Typography>
									<TimePicker
										value={formikProps.values[comp.id]}
										disabled={disabled || comp.disabled}
										label={comp.sublabel || ""}
										ampm={false}
										color="secondary.main"
										autoFocus={shouldAutoFocus(comp)}
										onChange={(value) => {
											formikProps.handleChange({
												target: {
													name: comp.id,
													value,
												},
											});
											if (comp.onChange) {
												comp.onChange(value);
											}
										}}
										sx={{
											"& .MuiInputBase-input": {
												color: "black",
											},
										}}
									/>
									{Boolean(formikProps.errors[comp.id]) && (
										<Typography textAlign="left" color="error" fontSize="small">
											{formikProps.errors[comp.id]}
										</Typography>
									)}
								</Grid2>
							)}
							{comp.customType === "numbered-input" && (
								<NumberedInput
									key={comp.id}
									id={comp.id}
									number={comp.number}
									label={comp.placeholder}
									placeholder={comp.placeholder}
									required={comp.required}
									variant={comp.variant || "standard"}
									className={classes.input}
									value={formikProps.values[comp.id]}
									numberPosition={comp.numberPosition || "left"}
									autoFocus={shouldAutoFocus(comp)}
									showDelete={comp.showDelete}
									onDelete={comp.onDelete ? () => {
										const newValues = { ...formikProps.values };
										delete newValues[comp.id];
										formikProps.setValues(newValues);
										comp.onDelete(comp.id);
									} : undefined}
									onChange={(event) => {
										formikProps.handleChange(event);
										if (comp.onChange) {
											comp.onChange(event);
										}
									}}
									error={Boolean(formikProps.errors[comp.id])}
									helperText={comp.helperText || formikProps.errors[comp.id]}
									disabled={disabled || comp.disabled}
								/>
							)}
							{comp.customType === "button"
								&& (
									<SecondaryBackgroundButton
										id={comp.id}
										type={comp.type}
										disabled={formikProps.isSubmitting || disabled || comp.disabled}
										className={classes.button}
										size="large"
										width="100%"
										title={comp.text}
									/>
								)}
							{comp.customType === "add-button" && (
								<AddButton
									tooltip={comp.tooltip}
									onClick={comp.onClick}
									disabled={disabled || comp.disabled}
									color={comp.color || "primary"}
								/>
							)}
							{comp.customType === "multiselect-dropdown" && (
								<Grid2 item className={classes.dropdown}>
									<MultiSelectDropdown
										id={comp.id}
										items={comp.items}
										value={formikProps.values[comp.id] || []}
										disabled={disabled || comp.disabled}
										size="medium"
										width="600px"
										label={comp.label}
										background="secondary"
										placeholder={comp.placeholder}
										required={comp.required}
										error={Boolean(formikProps.errors[comp.id])}
										helperText={comp.helperText || formikProps.errors[comp.id]}
										showSelectAll={comp.showSelectAll}
										showSelectEligible={comp.showSelectEligible}
										onSelectEligible={comp.onSelectEligible}
										excludedCodes={comp.excludedCodes}
										compactSelection={comp.compactSelection}
										renderSelectionSummary={comp.renderSelectionSummary}
										onChange={(event) => {
											formikProps.handleChange({
												target: {
													name: comp.id,
													value: event.target.value,
												},
											});
											if (comp.onChange) {
												comp.onChange(event);
											}
										}}
									/>
								</Grid2>
							)}
						</div>
					))}
				</form>
			)}
		</Formik>
	);
});

export default memo(Form);
